import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Cable, AlertTriangle, CheckCircle, HelpCircle, Edit3,
  Save, X, Zap, MapPin, Info
} from "lucide-react";

const WIRE_COLORS = {
  "Red": "#e84057",
  "Blue": "#4a9eff",
  "Brown": "#8B4513",
  "Brown/White Stripe": "#a0522d",
  "Dark Green": "#228B22",
  "Dark Green/White Stripe": "#32CD32",
  "Violet": "#8a2be2",
  "Violet/White Stripe": "#a855f7",
};

export default function WiringMap() {
  const [wiring, setWiring] = useState(null);
  const [editingPair, setEditingPair] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    axios.get(`${API}/wiring/can`).then(r => setWiring(r.data)).catch(() => {});
  }, []);

  const startEdit = (pair) => {
    setEditingPair(pair.id);
    setEditForm({ bus: pair.bus, notes: pair.notes, connects_to: pair.connects_to });
  };

  const saveEdit = async (pairId) => {
    try {
      await axios.put(`${API}/wiring/can/pair/${pairId}`, editForm);
      // Refresh
      const r = await axios.get(`${API}/wiring/can`);
      setWiring(r.data);
      setEditingPair(null);
      toast.success("Wiring notes updated");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  if (!wiring) return <div style={{ padding: 40, color: "#6b7280" }}>Loading wiring data...</div>;

  const connector = wiring.mcu_connector;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }}>
      <style>{`
        .wcard { background:#0e0e16; border:1px solid #1a1a28; border-radius:10px; padding:20px; }
        .wtitle { font-size:11px; text-transform:uppercase; letter-spacing:2px; color:#6b7280; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
        .wire-pair { border:1px solid #1a1a28; border-radius:10px; padding:16px; background:#0a0a12; transition:border-color .2s; }
        .wire-pair:hover { border-color:#2a2a3e; }
        .wire-pair.confirmed { border-color:rgba(45,212,168,0.3); }
        .wire-pair.unknown { border-color:rgba(245,166,35,0.2); }
        .wire-dot { width:14px; height:14px; border-radius:50%; flex-shrink:0; }
        .wire-stripe { width:14px; height:14px; border-radius:50%; flex-shrink:0; position:relative; overflow:hidden; }
        .wire-stripe::after { content:''; position:absolute; top:3px; left:0; right:0; height:3px; background:white; transform:rotate(-45deg); }
        .winput { font-size:12px; padding:6px 10px; border-radius:5px; border:1px solid #1e1e2e; background:#0a0a10; color:#e2e8f0; width:100%; outline:none; }
        .winput:focus { border-color:#4a9eff; }
        .wbtn { font-size:11px; padding:5px 12px; border-radius:4px; border:1px solid #1e1e2e; background:#14141e; color:#e2e8f0; cursor:pointer; display:inline-flex; align-items:center; gap:4px; }
        .wbtn:hover { background:#1c1c2a; }
        .obd-pin { display:flex; align-items:center; gap:10px; padding:8px 12px; border-radius:6px; background:#0a0a12; border:1px solid #1a1a28; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0", letterSpacing: 1, marginBottom: 4 }}>CAN Bus Wiring Map</h1>
        <p style={{ fontSize: 12, color: "#6b7280" }}>
          Physical wiring documentation - {connector.location}
        </p>
      </div>

      {/* Convention Info */}
      <div className="wcard" style={{ marginBottom: 16 }}>
        <div className="wtitle"><Info size={14} color="#4a9eff" /> Wiring Convention</div>
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className="wire-stripe" style={{ background: "#6b7280" }} />
            <span style={{ fontSize: 13, color: "#e2e8f0" }}>White Stripe = <strong style={{ color: "#2dd4a8" }}>CAN HI</strong></span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className="wire-dot" style={{ background: "#6b7280" }} />
            <span style={{ fontSize: 13, color: "#e2e8f0" }}>Solid Color = <strong style={{ color: "#4a9eff" }}>CAN LO</strong></span>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 11, color: "#6b7280", maxWidth: 400 }}>
            All twisted pairs route to the white female adapter under the MCU
          </div>
        </div>
      </div>

      {/* Warning */}
      <div style={{ padding: "12px 16px", background: "rgba(245,166,35,0.08)", border: "1px solid rgba(245,166,35,0.25)", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <AlertTriangle size={16} color="#f5a623" />
        <span style={{ fontSize: 12, color: "#f5a623" }}>{connector.obd2_warning}</span>
      </div>

      {/* Wire Pairs */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {connector.pairs.map((pair) => (
          <div key={pair.id} className={`wire-pair ${pair.confirmed ? "confirmed" : "unknown"}`} data-testid={`wire-pair-${pair.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {pair.confirmed ? (
                  <CheckCircle size={16} color="#2dd4a8" />
                ) : (
                  <HelpCircle size={16} color="#f5a623" />
                )}
                <span style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0" }}>
                  {pair.confirmed ? pair.bus : "Unidentified Bus"}
                </span>
              </div>
              {editingPair !== pair.id && (
                <button className="wbtn" onClick={() => startEdit(pair)} data-testid={`edit-${pair.id}`}>
                  <Edit3 size={11} /> Edit
                </button>
              )}
            </div>

            {/* Wire visualization */}
            <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                <div className="wire-stripe" style={{ background: WIRE_COLORS[pair.hi_color] || "#666" }} />
                <div>
                  <div style={{ fontSize: 11, color: "#2dd4a8", fontWeight: 600 }}>CAN HI</div>
                  <div style={{ fontSize: 12, color: "#e2e8f0" }}>{pair.hi_color}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                <div className="wire-dot" style={{ background: WIRE_COLORS[pair.lo_color] || "#666" }} />
                <div>
                  <div style={{ fontSize: 11, color: "#4a9eff", fontWeight: 600 }}>CAN LO</div>
                  <div style={{ fontSize: 12, color: "#e2e8f0" }}>{pair.lo_color}</div>
                </div>
              </div>
            </div>

            {/* Edit form or info */}
            {editingPair === pair.id ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 8, borderTop: "1px solid #1a1a28" }}>
                <div>
                  <label style={{ fontSize: 10, color: "#6b7280", display: "block", marginBottom: 4 }}>Bus Name</label>
                  <input className="winput" value={editForm.bus} onChange={e => setEditForm(p => ({ ...p, bus: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: "#6b7280", display: "block", marginBottom: 4 }}>Connects To</label>
                  <input className="winput" value={editForm.connects_to} onChange={e => setEditForm(p => ({ ...p, connects_to: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: "#6b7280", display: "block", marginBottom: 4 }}>Notes</label>
                  <input className="winput" value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="wbtn" style={{ borderColor: "rgba(45,212,168,0.3)", color: "#2dd4a8" }} onClick={() => saveEdit(pair.id)}>
                    <Save size={11} /> Save
                  </button>
                  <button className="wbtn" onClick={() => setEditingPair(null)}><X size={11} /> Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: "#6b7280", paddingTop: 8, borderTop: "1px solid #1a1a28" }}>
                <div>Connects to: <span style={{ color: "#e2e8f0" }}>{pair.connects_to}</span></div>
                <div style={{ marginTop: 4 }}>{pair.notes}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* OBD2 Pinout */}
      <div className="wcard" style={{ marginBottom: 16 }}>
        <div className="wtitle"><MapPin size={14} color="#f5a623" /> OBD2 Port Pinout (Standard)</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
          {Object.entries(wiring.obd2_pinout).map(([pin, info]) => (
            <div key={pin} className="obd-pin">
              <span style={{ fontSize: 14, fontWeight: 700, color: "#4a9eff", fontFamily: "monospace" }}>{pin.replace("pin_", "Pin ")}</span>
              <div>
                <div style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 600 }}>{info.function}</div>
                <div style={{ fontSize: 10, color: "#6b7280" }}>{info.color}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
